
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { Store } from '../types';
import { 
  Save, 
  Globe, 
  Palette, 
  Check, 
  Moon, 
  Sun, 
  Sparkles, 
  FileText, 
  Landmark, 
  Type, 
  Square, 
  Circle,
  Layers,
  Megaphone,
  BellRing,
  ShoppingBag,
  Eye,
  Zap
} from 'lucide-react';

const AdminSettings: React.FC = () => {
  const [store, setStore] = useState<Store>(storage.getStoreConfig());
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'branding' | 'marketing' | 'compliance' | 'security'>('general');

  const handleSave = () => {
    storage.saveStoreConfig(store);
    setIsSaved(true);
    // Notify app of branding/config changes
    window.dispatchEvent(new Event('store-branding-update'));
    window.dispatchEvent(new Event('storage'));
    setTimeout(() => setIsSaved(false), 2000);
  };

  const presetColors = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0f172a', '#db2777'];

  return (
    <div className="space-y-8 max-w-5xl animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Store Configuration</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Lynqar Theme Engine V2</p>
        </div>
        <button 
          onClick={handleSave}
          className="flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 group"
        >
          {isSaved ? <Check className="h-5 w-5" /> : <Save className="h-5 w-5 group-hover:rotate-12 transition-transform" />}
          {isSaved ? 'Deployed!' : 'Sync Changes'}
        </button>
      </div>

      <div className="flex gap-4 border-b border-gray-100 pb-1 overflow-x-auto">
         {(['general', 'branding', 'marketing', 'compliance', 'security'] as const).map((tab) => (
           <button 
             key={tab}
             onClick={() => setActiveTab(tab)}
             className={`px-6 py-3 text-xs font-black uppercase tracking-[0.2em] transition-all border-b-2 whitespace-nowrap ${
               activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
             }`}
           >
             {tab}
           </button>
         ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {activeTab === 'general' && (
            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-8 animate-in slide-in-from-left-4">
              <div className="flex items-center gap-3">
                 <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl"><Globe className="h-6 w-6" /></div>
                 <h3 className="text-xl font-black text-gray-900">Basic Identity</h3>
              </div>
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Store Name</label>
                  <input type="text" value={store.name} onChange={(e) => setStore({...store, name: e.target.value})} className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Slug (URL)</label>
                  <input type="text" value={store.slug} onChange={(e) => setStore({...store, slug: e.target.value})} className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'marketing' && (
            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-10 animate-in slide-in-from-left-4">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl"><Megaphone className="h-6 w-6" /></div>
                  <h3 className="text-xl font-black text-gray-900">Marketing & FOMO</h3>
               </div>

               <div className="p-8 bg-indigo-50 rounded-3xl border border-indigo-100 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm"><BellRing className="h-6 w-6" /></div>
                    <div>
                      <h4 className="font-black text-indigo-900">Live Social Proof Popups</h4>
                      <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-widest">Show recent sales and activity</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setStore({ ...store, socialProof: { ...(store.socialProof || { enabled: false, showRecentSales: true, showLiveViewers: true, showStockAlerts: true, intervalSeconds: 15 }), enabled: !store.socialProof?.enabled } })}
                    className={`w-14 h-8 rounded-full transition-all relative p-1 ${store.socialProof?.enabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                     <div className={`w-6 h-6 bg-white rounded-full transition-all ${store.socialProof?.enabled ? 'translate-x-6' : 'translate-x-0 shadow-md'}`}></div>
                  </button>
               </div>

               {store.socialProof?.enabled && (
                 <div className="space-y-8 animate-in zoom-in-95 duration-500">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="space-y-4">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Popup Interval (Seconds)</label>
                          <input 
                            type="number" 
                            min="5" max="60"
                            value={store.socialProof.intervalSeconds} 
                            onChange={e => setStore({...store, socialProof: {...store.socialProof!, intervalSeconds: parseInt(e.target.value)}})}
                            className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold"
                          />
                       </div>
                    </div>
                    <div className="space-y-4">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Display Components</label>
                       <div className="grid grid-cols-1 gap-4">
                          {[
                            { id: 'showRecentSales', label: 'Recent Purchase Notifications', icon: ShoppingBag },
                            { id: 'showLiveViewers', label: 'Live Viewer Counts', icon: Eye },
                            { id: 'showStockAlerts', label: 'Low Stock Urgency Alerts', icon: Zap }
                          ].map(item => (
                            <label key={item.id} className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 cursor-pointer transition-all">
                               <div className="flex items-center gap-4 text-sm font-bold text-gray-700">
                                  <item.icon className="h-5 w-5 text-gray-400" /> {item.label}
                                </div>
                               <input 
                                 type="checkbox" 
                                 checked={(store.socialProof as any)[item.id]} 
                                 onChange={() => setStore({...store, socialProof: {...store.socialProof!, [item.id]: !(store.socialProof as any)[item.id] }})}
                                 className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" 
                               />
                            </label>
                          ))}
                       </div>
                    </div>
                 </div>
               )}
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-8 animate-in slide-in-from-left-4">
               {/* Appearance Card */}
               <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-10">
                  <div className="flex items-center gap-3">
                     <div className="p-3 bg-purple-50 text-purple-600 rounded-2xl"><Palette className="h-6 w-6" /></div>
                     <h3 className="text-xl font-black text-gray-900">Appearance Engine</h3>
                  </div>

                  {/* Theme & Glassmorphism */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div className="space-y-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Theme Mode</label>
                        <div className="grid grid-cols-2 gap-4">
                           {['light', 'dark'].map((mode) => (
                             <button 
                               key={mode}
                               onClick={() => setStore({ ...store, branding: { ...store.branding!, themeMode: mode as any } })}
                               className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${store.branding?.themeMode === mode ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-200'}`}
                             >
                                {mode === 'light' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                                <span className="text-[10px] font-black uppercase">{mode}</span>
                             </button>
                           ))}
                        </div>
                     </div>
                     <div className="space-y-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Glassmorphism</label>
                        <button 
                          onClick={() => setStore({ ...store, branding: { ...store.branding!, glassmorphism: !store.branding?.glassmorphism } })}
                          className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${store.branding?.glassmorphism ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-200'}`}
                        >
                           <div className="flex items-center gap-3">
                              <Layers className="h-5 w-5" />
                              <span className="text-[10px] font-black uppercase">Frost Effect</span>
                           </div>
                           <div className={`w-8 h-4 rounded-full transition-all relative ${store.branding?.glassmorphism ? 'bg-blue-600' : 'bg-gray-300'}`}>
                              <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${store.branding?.glassmorphism ? 'left-4.5' : 'left-0.5'}`}></div>
                           </div>
                        </button>
                     </div>
                  </div>

                  {/* Colors */}
                  <div className="space-y-4">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Brand Core Color</label>
                     <div className="flex flex-wrap gap-4">
                        {presetColors.map((color) => (
                          <button key={color} onClick={() => setStore({ ...store, branding: { ...store.branding!, primaryColor: color } })} className={`w-12 h-12 rounded-2xl transition-all shadow-sm ${store.branding?.primaryColor === color ? 'ring-4 ring-offset-2 ring-blue-500 scale-110' : ''}`} style={{ backgroundColor: color }} />
                        ))}
                     </div>
                  </div>
               </div>

               {/* Typography & Geometry Card */}
               <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-10">
                  <div className="flex items-center gap-3">
                     <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl"><Type className="h-6 w-6" /></div>
                     <h3 className="text-xl font-black text-gray-900">Typography & Geometry</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                     <div className="space-y-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Font Family</label>
                        <div className="space-y-2">
                           {['Sans', 'Serif', 'Mono'].map((font) => (
                             <button 
                               key={font}
                               onClick={() => setStore({ ...store, branding: { ...store.branding!, fontFamily: font as any } })}
                               className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${store.branding?.fontFamily === font ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-50 text-gray-400 hover:border-gray-200'}`}
                             >
                                <span className={`text-sm font-black ${font === 'Serif' ? 'font-serif' : font === 'Mono' ? 'font-mono' : ''}`}>
                                   {font === 'Sans' ? 'Modern Sans' : font === 'Serif' ? 'Classic Serif' : 'Tech Mono'}
                                </span>
                                {store.branding?.fontFamily === font && <Check className="h-4 w-4" />}
                             </button>
                           ))}
                        </div>
                     </div>

                     <div className="space-y-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Button Geometry</label>
                        <div className="grid grid-cols-2 gap-3">
                           {[
                             { id: 'rounded-none', label: 'Sharp', icon: Square },
                             { id: 'rounded-xl', label: 'Rounded', icon: Square },
                             { id: 'rounded-3xl', label: 'Curvy', icon: Square },
                             { id: 'rounded-full', label: 'Pill', icon: Circle }
                           ].map((rad) => (
                             <button 
                               key={rad.id}
                               onClick={() => setStore({ ...store, branding: { ...store.branding!, buttonRadius: rad.id as any } })}
                               className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${store.branding?.buttonRadius === rad.id ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-50 text-gray-400 hover:border-gray-200'}`}
                             >
                                <rad.icon className="h-5 w-5" />
                                <span className="text-[10px] font-black uppercase">{rad.label}</span>
                             </button>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-8 animate-in slide-in-from-left-4">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl"><Landmark className="h-6 w-6" /></div>
                  <h3 className="text-xl font-black text-gray-900">Tax & Compliance</h3>
               </div>
               
               <div className="p-6 bg-emerald-50 rounded-3xl flex items-center justify-between">
                  <div>
                    <h4 className="font-black text-emerald-900">Enable Tax Calculation</h4>
                    <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest">Charge GST/VAT on all orders</p>
                  </div>
                  <button 
                    onClick={() => setStore({ ...store, taxConfig: { ...(store.taxConfig || { enabled: false, taxRate: 18 }), enabled: !store.taxConfig?.enabled } })}
                    className={`w-14 h-8 rounded-full transition-all relative p-1 ${store.taxConfig?.enabled ? 'bg-emerald-600' : 'bg-gray-200'}`}
                  >
                     <div className={`w-6 h-6 bg-white rounded-full transition-all ${store.taxConfig?.enabled ? 'translate-x-6' : 'translate-x-0 shadow-md'}`}></div>
                  </button>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">GSTIN / Tax ID</label>
                    <input 
                      type="text" 
                      placeholder="e.g. 27AAAAA0000A1Z5"
                      value={store.taxConfig?.taxNumber || ''} 
                      onChange={(e) => setStore({...store, taxConfig: { ...(store.taxConfig || { enabled: false, taxRate: 18 }), taxNumber: e.target.value }})} 
                      className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Default Tax Rate (%)</label>
                    <input 
                      type="number" 
                      value={store.taxConfig?.taxRate || 0} 
                      onChange={(e) => setStore({...store, taxConfig: { ...(store.taxConfig || { enabled: false, taxRate: 18 }), taxRate: parseFloat(e.target.value) }})} 
                      className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold" 
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Registered Business Address</label>
                    <textarea 
                      rows={3}
                      value={store.taxConfig?.businessAddress || ''} 
                      onChange={(e) => setStore({...store, taxConfig: { ...(store.taxConfig || { enabled: false, taxRate: 18 }), businessAddress: e.target.value }})} 
                      className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold resize-none" 
                    />
                  </div>
               </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
           {/* Live Preview Card */}
           <div className="bg-gray-950 p-8 rounded-[40px] text-white shadow-2xl space-y-8 relative overflow-hidden border border-gray-800">
              <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Style Preview</h4>
              <div className="space-y-6">
                 <div className={`p-6 rounded-brand border border-white/10 ${store.branding?.glassmorphism ? 'bg-white/10 backdrop-blur-md' : 'bg-white/5'}`}>
                    <div className="flex items-center gap-3 mb-4">
                       <div className="w-10 h-10 rounded-brand bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/50" style={{ backgroundColor: store.branding?.primaryColor }}>
                          <Sparkles className="h-5 w-5" />
                       </div>
                       <div>
                          <p className={`font-black text-sm ${store.branding?.fontFamily === 'Serif' ? 'font-serif' : store.branding?.fontFamily === 'Mono' ? 'font-mono' : ''}`}>Product Title</p>
                          <p className="text-[10px] text-gray-500 font-bold uppercase">Secondary Text</p>
                       </div>
                    </div>
                    <button className="w-full py-3 rounded-brand font-black text-[10px] uppercase tracking-widest text-white shadow-xl transition-all" style={{ backgroundColor: store.branding?.primaryColor }}>
                       Primary Action
                    </button>
                 </div>
                 <p className="text-xs text-gray-500 text-center font-medium">This is how your components will look across the entire storefront.</p>
              </div>
           </div>

           <div className="bg-emerald-950 text-white p-8 rounded-[40px] shadow-2xl space-y-6 relative overflow-hidden group border border-emerald-800">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><FileText className="h-24 w-24" /></div>
              <div className="relative">
                <h4 className="font-black text-xl mb-2">Compliance Shield</h4>
                <p className="text-emerald-400 text-sm leading-relaxed mb-6">Your store's tax and invoicing setup is active. Ensure your Business Address is accurate for proper legal invoicing.</p>
                <div className="flex items-center gap-3">
                   <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center"><Check className="h-5 w-5" /></div>
                   <div>
                      <p className="text-[10px] font-black uppercase">Tax Status</p>
                      <p className="font-bold">{store.taxConfig?.enabled ? 'Fully Compliant' : 'Self-Declaration'}</p>
                   </div>
                </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
